/**
 * Persistencia de "este tour ya lo cerre / ya lo vi", por usuario.
 *
 * Por que existe: en Sistema.jsx los cierres vivian en `useState` puro
 * (`dismissedModuleOnboarding`, `dismissedFirstSaleOnboarding`). Eran estado en
 * memoria, asi que cerrar la guia duraba hasta el proximo refresh: al volver a
 * entrar el tour arrancaba de cero, una y otra vez.
 *
 * Se usa la tabla `onboarding_progress` (PK user_id + key), que ya existe y ya
 * la usa el motor de onboarding. Es POR USUARIO, no por organizacion: si el
 * dueño cierra un tour, los empleados igual lo ven la primera vez. Guardarlo en
 * `organizations.module_onboarding_statuses` habria hecho justo lo contrario.
 *
 * localStorage queda como cache optimista para que el primer render no
 * parpadee (la lectura a la base es asincronica y el cartel se monta antes).
 */
import { supabase } from '../supabase';

export const TOUR_DISMISSALS_KEY = 'tour_dismissals';

const cacheKey = (userId) => `impulza:tour-dismissals:${userId || 'anon'}`;
const isBrowser = () => typeof window !== 'undefined';

const VACIO = { modules: {}, firstSale: {} };

const normalizar = (state) => ({
  modules: (state && typeof state.modules === 'object' && state.modules) || {},
  firstSale: (state && typeof state.firstSale === 'object' && state.firstSale) || {},
});

/** Lectura sincronica del cache local. Sirve para el primer render. */
export function leerTourDismissalsCache(userId) {
  if (!isBrowser()) return VACIO;
  try {
    return normalizar(JSON.parse(window.localStorage.getItem(cacheKey(userId)) || '{}'));
  } catch {
    return VACIO;
  }
}

const escribirCache = (userId, state) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(state));
  } catch {
    /* cuota llena o modo privado: el estado igual vive en la base */
  }
};

/**
 * Trae los cierres persistidos del usuario y refresca el cache local.
 * Nunca lanza: si la base no responde, se sigue con lo que haya en cache.
 */
export async function cargarTourDismissals(userId) {
  if (!userId) return VACIO;
  try {
    const { data, error } = await supabase
      .from('onboarding_progress')
      .select('state')
      .eq('user_id', userId)
      .eq('key', TOUR_DISMISSALS_KEY)
      .maybeSingle();
    if (error) return leerTourDismissalsCache(userId);
    const state = normalizar(data?.state);
    escribirCache(userId, state);
    return state;
  } catch {
    return leerTourDismissalsCache(userId);
  }
}

/**
 * Marca un tour como cerrado y lo persiste.
 *
 * @param {object}  args
 * @param {string}  args.userId
 * @param {string} [args.organizationId]
 * @param {'modules'|'firstSale'} args.tipo
 * @param {string}  args.id           modulo (`productos`) o stepId del recorrido
 * @param {object} [args.estadoActual] estado ya conocido, para no releer
 * @returns {Promise<object>} el estado resultante
 */
export async function marcarTourCerrado({
  userId,
  organizationId = null,
  tipo,
  id,
  estadoActual = null,
} = {}) {
  if (!id || (tipo !== 'modules' && tipo !== 'firstSale')) return normalizar(estadoActual);

  const base = normalizar(estadoActual || leerTourDismissalsCache(userId));
  const siguiente = {
    ...base,
    [tipo]: { ...base[tipo], [id]: true },
  };

  // El cache se escribe siempre: la UI tiene que responder aunque la base falle.
  escribirCache(userId, siguiente);
  if (!userId) return siguiente;

  try {
    await supabase.from('onboarding_progress').upsert([{
      user_id: userId,
      organization_id: organizationId,
      key: TOUR_DISMISSALS_KEY,
      state: siguiente,
      updated_at: new Date().toISOString(),
    }], { onConflict: 'user_id,key' });
  } catch {
    /* el cierre ya quedo en cache; se reintenta solo en el proximo cierre */
  }

  return siguiente;
}

/** ¿Este tour ya fue cerrado por el usuario? */
export function tourEstaCerrado(state, tipo, id) {
  if (!id) return false;
  return Boolean(normalizar(state)?.[tipo]?.[id]);
}
